import { create } from 'zustand';

import { getRfqConfigurationsQuery } from 'Services/Query/Auto-RFQ';

import {
  DEFAULT_PRICING_SETTINGS,
  fetchPricingSettings,
  getQuoteConfigurationsQuery
} from 'Services';
import { ConfigurationProps } from '../interfaces';

// DEV-540: pricing behavior moved into its own `pricing-configuration` document,
// so PricingMode now lives with that service. Re-exported here because
// components import it from this store.
export { PricingMode } from 'Services';

interface ConfigState extends ConfigurationProps {
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;
}

interface ConfigStore extends ConfigState {
  fetchConfigurations: () => Promise<void>;
  updateConfiguration: (config: Partial<ConfigurationProps>) => void;
  reset: () => void;
}

const initialState: ConfigState = {
  _id: '',
  rfqListFields: [],
  validUntil: undefined,
  enableCRMIntegration: false,
  enableOrderHistory: false,
  enableApprovalProcess: false,
  enableOpportunity: false,
  enableShipTo: false,
  marginPercentage: 0,
  enableSpecialPurchaseRequest: false,
  enableEquipmentRequest: false,
  enableValidityDate: false,
  enableNewProspect: false,
  pricing: DEFAULT_PRICING_SETTINGS,
  rfqToQuoteEnabled: false,
  enableAccountSelection: false,
  enableProspect: false,
  requireSegmentForProspect: false,
  defaultSegment: null,
  showManufacturerDetails: false,
  // Opt-out flag (DEV-744): absent from a tenant's config means "keep showing
  // the Competitor field", which is how every tenant behaved before it existed.
  enableCompetitor: true,
  alternateItemsEnabled: false,
  isLoading: false,
  isInitialized: false,
  error: null
};

// RQF Quote configurations
export const useRfqConfigStore = create<ConfigStore>((set, get) => ({
  ...initialState,

  fetchConfigurations: async () => {
    if (get().isInitialized) return;

    set({ isLoading: true, error: null });

    try {
      const query = getRfqConfigurationsQuery();
      const response = await query.queryFn();

      if (response) {
        // Pricing behavior lives in its own `pricing-configuration` document
        // (DEV-540). The nested `pricing` object still on this response is the
        // retired fallback, used only until that endpoint is deployed.
        const pricing = await fetchPricingSettings(response.pricing);

        set({
          ...response,
          pricing,
          isLoading: false,
          isInitialized: true,
          error: null
        });
      }
    } catch (error) {
      set({
        isLoading: false,
        error:
          error instanceof Error
            ? error.message
            : 'Failed to fetch configurations'
      });
    }
  },

  updateConfiguration: (config: Partial<ConfigurationProps>) =>
    set(state => ({ ...state, ...config })),

  reset: () => set({ ...initialState })
}));

// Quote configurations
export const useQuoteConfigStore = create<ConfigStore>((set, get) => ({
  ...initialState,

  fetchConfigurations: async () => {
    if (get().isInitialized) return;

    set({ isLoading: true, error: null });

    try {
      const query = getQuoteConfigurationsQuery();
      const response = await query.queryFn();

      if (response) {
        // Pricing behavior lives in its own `pricing-configuration` document
        // (DEV-540). The nested `pricing` object still on this response is the
        // retired fallback, used only until that endpoint is deployed.
        const pricing = await fetchPricingSettings(response.pricing);

        set({
          ...response,
          pricing,
          isLoading: false,
          isInitialized: true,
          error: null
        });
      }
    } catch (error) {
      set({
        isLoading: false,
        error:
          error instanceof Error
            ? error.message
            : 'Failed to fetch configurations'
      });
    }
  },

  updateConfiguration: (config: Partial<ConfigurationProps>) =>
    set(state => ({ ...state, ...config })),

  reset: () => set({ ...initialState })
}));
